import React, { useState } from "react";
import { Search, Phone, CheckCircle2, Truck, Clock, Package } from "lucide-react";
import { toast } from "sonner";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { whatsappLink } from "../whatsapp";

export default function TrackOrder() {
  const [orderId, setOrderId] = useState("");
  const [phone, setPhone] = useState("");
  const [demo, setDemo] = useState(null);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!orderId || !phone) {
      toast.error("Need both", { description: "Enter your order ID and phone number." });
      return;
    }
    // Phase 2: hit /api/orders/track
    setDemo({
      id: orderId,
      placed: new Date(Date.now() - 26 * 3600 * 1000).toISOString(),
      eta: "Today, before 5 pm",
      stages: [
        { label: "Order received", at: "Today, 9:14 AM", done: true, icon: CheckCircle2 },
        { label: "Hand-harvested & packed", at: "Today, 10:42 AM", done: true, icon: Package },
        { label: "Out for delivery", at: "Today, 2:10 PM", done: true, icon: Truck, current: true },
        { label: "Delivered", at: "ETA before 5 PM", done: false, icon: CheckCircle2 },
      ],
    });
  };

  return (
    <div data-testid="track-page" className="relative">
      <span className="stamp-num absolute top-6 right-6 md:right-12">N° T</span>

      <section className="max-w-3xl mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-12">
        <p className="section-num">
          <strong>·</strong> Track your order
        </p>
        <h1 className="font-display mt-5 text-display-lg text-ink tracking-tightest fade-up">
          Where's my
          <br />
          <span className="font-italic text-coral">basket?</span>
        </h1>
        <p className="mt-6 text-ink/65 leading-relaxed text-[15px] fade-up-d1 max-w-lg">
          Enter your order ID and the phone number you placed the order with.
          We'll show you the live status. (Real lookup connects in Phase 2.)
        </p>

        <form
          data-testid="track-form"
          onSubmit={onSubmit}
          className="mt-10 bg-boneSoft border border-ink/10 rounded-4xl p-6 md:p-8 space-y-5"
        >
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] text-muted block mb-2">
              Order ID
            </label>
            <input
              data-testid="track-order-id"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="ORG-2026-0001"
              className="w-full bg-bone border border-ink/15 rounded-full px-5 py-3 outline-none focus:border-coral text-ink placeholder:text-muted transition-colors"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] text-muted block mb-2">
              Phone (used at order)
            </label>
            <input
              data-testid="track-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              type="tel"
              placeholder="98765 43210"
              className="w-full bg-bone border border-ink/15 rounded-full px-5 py-3 outline-none focus:border-coral text-ink placeholder:text-muted transition-colors"
            />
          </div>
          <button
            type="submit"
            data-testid="track-submit"
            className="btn-primary"
          >
            Find my order
            <span className="knob">
              <Search className="h-4 w-4" />
            </span>
          </button>
        </form>

        <div className="mt-6 flex items-center gap-4 text-sm">
          <span className="text-muted">Or</span>
          <a
            href={whatsappLink("Hi Organo Mushroom 👋\n\nCould you please share the status of my recent order?")}
            target="_blank"
            rel="noreferrer"
            className="link-underline inline-flex items-center gap-2 text-whatsapp font-medium"
          >
            <WhatsAppIcon className="h-4 w-4" /> Ask us on WhatsApp
          </a>
          <span className="text-muted">·</span>
          <a href="tel:+918208805487" className="link-underline inline-flex items-center gap-2 text-ink font-medium">
            <Phone className="h-4 w-4" /> Call us
          </a>
        </div>
      </section>

      {/* Demo timeline shown after submit */}
      {demo && (
        <section className="max-w-3xl mx-auto px-6 md:px-12 pb-32 fade-up">
          <div className="rounded-4xl bg-ink text-bone p-8 md:p-10 shadow-hoverCard">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-bone/55">
                  Order
                </p>
                <p className="font-display text-3xl mt-2 tracking-tightest">
                  {demo.id || "ORG-2026-0001"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.28em] text-bone/55">
                  ETA
                </p>
                <p className="font-italic text-2xl text-butter mt-1.5">{demo.eta}</p>
              </div>
            </div>

            <ol className="mt-10 relative border-l-2 border-butter/30 pl-7 space-y-7">
              {demo.stages.map((s, i) => (
                <li key={i} className="relative">
                  <span
                    className={`absolute -left-[32px] h-5 w-5 rounded-full ring-4 ring-ink grid place-items-center ${
                      s.done ? "bg-butter" : "bg-bone/15"
                    }`}
                  >
                    {s.current && (
                      <span className="h-2 w-2 rounded-full bg-coral pulse-dot" />
                    )}
                  </span>
                  <p className={`font-display text-xl tracking-tightest ${s.done ? "text-bone" : "text-bone/40"}`}>
                    {s.label}
                  </p>
                  <p className="text-[12px] uppercase tracking-[0.22em] text-bone/55 mt-1 inline-flex items-center gap-2">
                    <Clock className="h-3 w-3" /> {s.at}
                  </p>
                </li>
              ))}
            </ol>

            <p className="mt-10 text-bone/55 text-xs">
              This is a sample timeline. Once Phase-2 backend goes live, this
              page will show your real order status by ID + phone.
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
